// @flow
import React, { Component } from 'react';
import { withHelmet } from 'infrastructure/seo/Helmet';
import type { CardType, NewsNunType } from 'types/PageList';
import type { BlocContent } from 'types/NewsletterBloc';
import type { ArticleType } from 'types/Article';
import type { Status } from 'types/Status';

import { STATUS_UNKNOWN, STATUS_LOADING } from 'constants/statusConstants';
import PageNotFound from 'components/app/PageNotFound';
import Loading from 'components/fragments/Loading';

import PageHubCentenaire from 'containers/centenaire/PageHubCentenaireContainer';
import PageHubEntreprise from 'components/page/template/PageHubEntreprise';
import PageHubSelections from 'components/page/template/PageHubSelections';
import PageHubDefault from 'components/page/template/PageHubDefault';
import PageJeuConcours from 'components/page/template/PageJeuConcours';
import PageGrandSoutien from 'components/page/template/PageGrandSoutien';
import PageRematchContainer from 'containers/page/PageRematchContainer';
import { getMetaBySlug } from 'utils/seoUtils';

export type StateProps = {
  template: string,
  title: string,
  description: Array<BlocContent>,
  meta_title: string,
  meta_description: string,
  label: {
    id: number,
    name: string,
    slug: string
  },
  featured_media: {
    src: string,
    legend: string
  },
  liste_enfants: Array<CardType>,
  liste_cards: Array<CardType>,
  articles: Array<ArticleType>,
  news: Array<NewsNunType>,
  redirection: string,
  status: Status
};

export type DispatchProps = {
  fetchPageList: (slug: string) => void
};

type Props = {
  match: {
    params: {
      slug: string
    }
  },
  pageSlug: string
} & DispatchProps &
  StateProps;

class PageList extends Component<Props> {
  componentDidMount() {
    const { slug } = this.props.match.params;
    if (slug) {
      this.props.fetchPageList(slug);
    } else {
      this.props.fetchPageList(this.props.pageSlug);
    }
  }

  shouldComponentUpdate(nextProps: Props) {
    const { slug } = this.props.match.params;

    if (nextProps !== this.props) {
      if (nextProps.match.params.slug !== slug) {
        if (nextProps.match.params.slug) {
          this.props.fetchPageList(nextProps.match.params.slug);
          return true;
        }
      }
      return true;
    }
    return false;
  }

  render() {
    const { props } = this;
    const { status, template } = props;

    if (status === STATUS_LOADING) {
      return <Loading isBlue backgroundColor={'#013974'} />;
    }

    if (status === STATUS_UNKNOWN) {
      return <PageNotFound noMargin />;
    }

    switch (template) {
      case 'page-hub_entreprises':
        return <PageHubEntreprise {...props} />;
      case 'page-centenaire':
        return <PageHubCentenaire {...props} />;
      case 'page-hub_selections':
        return <PageHubSelections {...props} />;
      case 'page-jeu-concours':
          return <PageJeuConcours {...props} />;
      case 'page-grand_soutien':
          return <PageGrandSoutien {...props} />;
      case 'page-rematch':
          return <PageRematchContainer {...props} />;
      default:
        return <PageHubDefault {...props} />;
    }
  }
}

export default withHelmet(PageList)((Helmet, pageProps) => {
  const { slug } = pageProps.match.params;
  const meta = getMetaBySlug(slug);
  const ogimage = pageProps.featured_media.src;
  const ogimagetitle = pageProps.featured_media.title;

  return (
    <Helmet>
      <title>{meta.title(pageProps.meta_title) || pageProps.meta_title}</title>
      <meta name={'description'} content={meta.description(pageProps.meta_description) || pageProps.meta_description} />
      <meta property={'og:url'} content={window.location.href} />
      <meta property={'og:title'} content={meta.title(pageProps.meta_title) || pageProps.meta_title} />
      <meta property={'og:description'} content={meta.description(pageProps.meta_description) || pageProps.meta_description} />
      {ogimage && <meta property={'og:image'} content={ogimage} />}
      {ogimage && <meta property={'og:image:url'} content={ogimage} />}
      {(ogimage && ogimagetitle) && <meta property={'og:image:title'} content={ogimagetitle} />}
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
